// La pantalla de entrada de Tienda online: las tiendas conectadas y sus productos, leídos en vivo
// (ADR-2026-10-29-tienda-online-refleja-la-tienda). Y lo que pasa al tocar un producto: traerlo a Atlas
// si no está, y abrir su ficha en el lanzamiento invisible de esa tienda.

import { atlasBackendFetch, atlasBackendGet } from "../../lib/atlasBackend";
import { fireTnImport } from "../../lib/tiendaNube";
import { importarDeMl, esperarImportDeMl } from "../../lib/mercadoLibreCatalogo";
import { supabase } from "../../lib/supabase";
import {
  agregarProductosDelCatalogo,
  fetchCategorias,
  fetchLanzamiento,
} from "./lanzamientosApi";
import { cambiosContraLaTienda } from "./cambiosContraLaTienda";

export type PlataformaDeTienda = "tiendanube" | "mercadolibre";

export interface TiendaConectada {
  plataforma: PlataformaDeTienda;
  /** El nombre de la tienda (Tiendanube) o el usuario (Mercado Libre). */
  nombre: string | null;
  conectadaEn: string;
  /** Mercado Libre pide volver a conectar: la lista no se puede leer hasta entonces. */
  reconectar: boolean;
}

export interface ImportDeTn {
  tn_product_id: number;
  nombre: string;
  imgs: string[];
  variantes: Array<{ nombre: string; imgs: string[] }>;
}

/**
 * Lo que le falta para poder venderse (lo calcula el backend): fotos y precio, el mismo criterio que la
 * ficha para publicar (`problemasDeFicha`). Descripción y categoría son opcionales y no cuentan.
 */
export type DatoQueFalta = "fotos" | "precio";

/** El texto de la pill de lo que falta: "Falta el precio", "Faltan las fotos", "Faltan fotos y precio". */
export function textoDeLoQueFalta(faltan: readonly DatoQueFalta[]): string | null {
  const fotos = faltan.includes("fotos");
  const precio = faltan.includes("precio");
  if (fotos && precio) return "Faltan fotos y precio";
  if (fotos) return "Faltan las fotos";
  if (precio) return "Falta el precio";
  return null;
}

export interface ProductoDeLaTienda {
  idExterno: string;
  nombre: string;
  foto: string | null;
  precio: number | null;
  precioPromocional: number | null;
  moneda: string | null;
  /** null = sin límite. */
  stock: number | null;
  variantes: number;
  estado: "publicado" | "oculto" | "pausado";
  url: string | null;
  /** El producto de Atlas, o null si todavía no se trajo. */
  productoId: string | null;
  importar: ImportDeTn | null;
  faltan: DatoQueFalta[];
  /** Ids de sus categorías en la tienda (Tiendanube). */
  categorias: string[];
  /** Sólo los nuevos: sus categorías de Atlas (todavía no tienen las de la tienda). */
  categoriasDeAtlas?: string[];
  /** Cuándo se modificó por última vez (en la tienda, o en Atlas si es nuevo). ISO. Para ordenar. */
  actualizadoEn: string | null;
  /**
   * Sólo los productos nuevos que todavía no están en la tienda (se crean al sincronizar): a dónde va
   * tocarlo, su ficha en la lista de cambios. Los de la tienda se abren con `abrirProductoDeTiendanube`.
   */
  ruta?: string;
}

export interface PaginaDeProductos {
  productos: ProductoDeLaTienda[];
  hayMas: boolean;
  /** Mercado Libre lo sabe; Tiendanube no lo dice en esta lectura. */
  total: number | null;
}

/** Un error de lectura con el código del backend (`sin_conexion`, `reconectar`, …). */
export class ErrorDeLaTienda extends Error {
  constructor(
    readonly codigo: string,
    mensaje?: string,
  ) {
    super(mensaje || "No pudimos leer tu tienda. Probá de nuevo en un rato.");
  }
}

async function pedir<T>(ruta: string): Promise<T> {
  let r: Response;
  try {
    r = await atlasBackendGet(ruta);
  } catch {
    throw new ErrorDeLaTienda("red", "No pudimos conectarnos. Revisá tu conexión y probá de nuevo.");
  }
  const cuerpo = (await r.json().catch(() => null)) as ({ error?: string; message?: string } & T) | null;
  if (!r.ok || !cuerpo) throw new ErrorDeLaTienda(cuerpo?.error ?? `http_${r.status}`, cuerpo?.message);
  return cuerpo;
}

const conMarca = (brandId: string | null, extra: Record<string, string> = {}) => {
  const q = new URLSearchParams(extra);
  if (brandId) q.set("brand_id", brandId);
  return q.toString();
};

/** Las tiendas conectadas de la marca, la primera que se conectó primero. */
export async function fetchTiendasConectadas(brandId: string | null): Promise<TiendaConectada[]> {
  const r = await pedir<{
    tiendas?: Array<{ plataforma: PlataformaDeTienda; nombre: string | null; conectada_en: string; reconectar: boolean }>;
  }>(`/v1/tienda/tiendas?${conMarca(brandId)}`);
  const tiendas = (r.tiendas ?? []).map((t) => ({
    plataforma: t.plataforma,
    nombre: t.nombre,
    conectadaEn: t.conectada_en,
    reconectar: t.reconectar,
  }));
  return tiendas;
}

/**
 * El panel "Filtrar" de Tienda online (ADR-2026-10-30-filtrar-productos). Tiendanube los filtra en su lado.
 * Ausente = "Todos".
 */
export interface FiltrosDeLaTienda {
  stock?: "en" | "fuera" | "cantidad";
  stockMin?: number;
  stockMax?: number;
  precio?: "promocional" | "no_promocional";
  visibilidad?: "visible" | "hidden" | "unlisted";
  envio?: "gratis" | "pago";
  medidas?: "sin_dimensiones" | "sin_peso" | "sin_peso_ni_dimensiones";
}

/** Cuántos filtros del panel hay puestos (el número del botón). */
export const cuantosFiltros = (f: FiltrosDeLaTienda): number =>
  [f.stock, f.precio, f.visibilidad, f.envio, f.medidas].filter(Boolean).length;

export async function fetchProductosDeLaTienda(p: {
  brandId: string | null;
  plataforma: PlataformaDeTienda;
  pagina: number;
  q?: string;
  /** Id de la categoría en Tiendanube (filtra allá). */
  categoria?: string | null;
  filtros?: FiltrosDeLaTienda;
}): Promise<PaginaDeProductos> {
  const extra: Record<string, string> = { plataforma: p.plataforma, pagina: String(p.pagina) };
  if (p.q?.trim()) extra.q = p.q.trim();
  if (p.categoria) extra.categoria = p.categoria;
  const f = p.filtros ?? {};
  if (f.stock) extra.stock = f.stock;
  if (f.stock === "cantidad" && typeof f.stockMin === "number") extra.stock_min = String(f.stockMin);
  if (f.stock === "cantidad" && typeof f.stockMax === "number") extra.stock_max = String(f.stockMax);
  if (f.precio) extra.precio = f.precio;
  if (f.visibilidad) extra.visibilidad = f.visibilidad;
  if (f.envio) extra.envio = f.envio;
  if (f.medidas) extra.medidas = f.medidas;
  const r = await pedir<{ productos?: unknown[]; hay_mas?: boolean; total?: number | null }>(
    `/v1/tienda/productos?${conMarca(p.brandId, extra)}`,
  );
  return {
    productos: (r.productos ?? []).map(productoDesdeElBackend),
    hayMas: r.hay_mas === true,
    total: typeof r.total === "number" ? r.total : null,
  };
}

function productoDesdeElBackend(crudo: unknown): ProductoDeLaTienda {
  const x = crudo as Record<string, unknown>;
  const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : null);
  return {
    idExterno: String(x.id_externo),
    nombre: String(x.nombre ?? ""),
    foto: typeof x.foto === "string" ? x.foto : null,
    precio: num(x.precio),
    precioPromocional: num(x.precio_promocional),
    moneda: typeof x.moneda === "string" ? x.moneda : null,
    stock: num(x.stock),
    variantes: num(x.variantes) ?? 1,
    estado: x.estado === "oculto" || x.estado === "pausado" ? x.estado : "publicado",
    url: typeof x.url === "string" ? x.url : null,
    productoId: typeof x.producto_id === "string" ? x.producto_id : null,
    importar: (x.importar as ImportDeTn | undefined) ?? null,
    faltan: Array.isArray(x.faltan)
      ? (x.faltan as unknown[]).filter((d): d is DatoQueFalta => d === "fotos" || d === "precio")
      : [],
    categorias: Array.isArray(x.categorias) ? (x.categorias as unknown[]).map(String) : [],
    actualizadoEn: typeof x.actualizado_en === "string" ? x.actualizado_en : null,
  };
}

/**
 * Borra un producto de la Tiendanube (ADR-2026-10-30-eliminar-un-producto). No se recupera. En Atlas sigue en
 * el catálogo, sin vínculo con la tienda.
 */
export async function borrarDeLaTienda(brandId: string | null, tnProductId: number): Promise<void> {
  let r: Response;
  try {
    r = await atlasBackendFetch("/v1/tienda/productos/borrar", { brand_id: brandId, tn_product_id: tnProductId });
  } catch {
    throw new ErrorDeLaTienda("red", "No pudimos conectarnos. Revisá tu conexión y probá de nuevo.");
  }
  if (!r.ok) {
    const cuerpo = (await r.json().catch(() => null)) as { error?: string; message?: string } | null;
    throw new ErrorDeLaTienda(cuerpo?.error ?? `http_${r.status}`, cuerpo?.message ?? "No se pudo eliminar. Probá de nuevo.");
  }
}

/** Con qué tienda arranca la pantalla: la que se eligió antes si sigue conectada; si no, la primera. */
export function tiendaInicial(
  tiendas: readonly TiendaConectada[],
  recordada: string | null,
): PlataformaDeTienda | null {
  if (tiendas.length === 0) return null;
  return tiendas.find((t) => t.plataforma === recordada)?.plataforma ?? tiendas[0]!.plataforma;
}

// ── Sin sincronizar ────────────────────────────────────────────────────────

/**
 * Los productos de Tiendanube (por id de la tienda) que tienen cambios guardados en Atlas que la tienda
 * todavía no tiene: su fila en el lanzamiento invisible no está publicada y tiene fotos nuevas o algún
 * campo distinto de con qué arrancó la ficha (`tiendaBase`). No lee la tienda: compara contra la base.
 */
export interface SinSincronizar {
  /** Los de la tienda con cambios guardados, por su id de Tiendanube. */
  ids: Set<string>;
  /** Los nuevos: todavía no existen en la tienda, así que la lectura de la tienda no los trae. */
  nuevos: ProductoDeLaTienda[];
  /** Cuándo se editó en Atlas cada uno de los de la tienda con cambios (por su id de Tiendanube). */
  editadosEn: Map<string, string>;
}

export async function sinSincronizarEnTiendanube(companyId: string, brandId: string | null): Promise<SinSincronizar> {
  let q = supabase
    .from("lanzamientos")
    .select("id")
    .eq("id_company", companyId)
    .eq("canal", "tiendanube")
    .eq("de_la_tienda", true);
  q = brandId ? q.eq("brand_id", brandId) : q.is("brand_id", null);
  const { data, error } = await q.limit(1);
  const id = (data?.[0] as { id?: string } | undefined)?.id;
  if (error || !id) return { ids: new Set(), nuevos: [], editadosEn: new Map() };
  const [detalle, arbol, fechas] = await Promise.all([
    fetchLanzamiento(id, companyId),
    fetchCategorias(companyId, brandId),
    supabase.from("lanzamientos_productos").select("id, updated_at, created_at").eq("lanzamiento_id", id),
  ]);
  // Cuándo se tocó cada fila en Atlas: ordena la lista "lo último modificado primero".
  const fechaDe = new Map(
    ((fechas.data ?? []) as Array<{ id: string; updated_at: string | null; created_at: string | null }>).map((r) => [
      r.id,
      r.updated_at ?? r.created_at ?? null,
    ]),
  );
  const editadosEn = new Map<string, string>();
  const out = new Set<string>();
  const nuevos: ProductoDeLaTienda[] = [];
  for (const p of detalle?.productos_ ?? []) {
    // No depende de "publicado": al sincronizar la ficha vuelve a ser la tienda (`reiniciarConLaTienda`),
    // así que uno publicado sólo cuenta si se lo volvió a tocar.
    if (p.tnProductId == null) {
      if (p.estado === "publicado") continue;
      if (!p.productoId) continue;
      const precio = p.precio != null && p.precio !== "" ? Number(p.precio) : null;
      nuevos.push({
        idExterno: `nuevo:${p.id}`,
        nombre: p.nombre.trim() || "Producto sin nombre",
        foto: p.imagenes[0]?.thumbUrl || null,
        precio: precio !== null && Number.isFinite(precio) ? precio : null,
        precioPromocional: null,
        moneda: null,
        stock: p.stock ?? null,
        variantes: Math.max(1, p.variantes.length),
        estado: "publicado",
        url: null,
        productoId: p.productoId,
        importar: null,
        faltan: [
          ...(p.imagenes.length === 0 ? (["fotos"] as const) : []),
          ...(precio === null ? (["precio"] as const) : []),
        ],
        categorias: [],
        categoriasDeAtlas: p.categorias,
        actualizadoEn: fechaDe.get(p.id) ?? null,
        ruta: `/lanzamientos/${id}?producto=${p.id}`,
      });
      continue;
    }
    const tocado =
      p.imagenes.length > 0 ||
      (p.fotosABorrar?.length ?? 0) > 0 ||
      (!!p.tiendaBase && cambiosContraLaTienda(p, p.tiendaBase, arbol).cambios.length > 0);
    if (tocado) {
      out.add(String(p.tnProductId));
      const f = fechaDe.get(p.id);
      if (f) editadosEn.set(String(p.tnProductId), f);
    }
  }
  return { ids: out, nuevos, editadosEn };
}

// ── Abrir un producto ──────────────────────────────────────────────────────

const ESPERA_IMPORT_MS = 90_000;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * El producto de Atlas de uno de Tiendanube que todavía no está: lo manda a importar y espera a que
 * aparezca. El import es de fondo (contesta al instante), así que se mira la base cada 2 s.
 */
async function traerDeTiendanube(
  producto: ProductoDeLaTienda,
  destino: { userId: string | undefined; companyId: string; brandId: string | null },
): Promise<string> {
  if (!producto.importar) throw new ErrorDeLaTienda("sin_import", "No pudimos traer este producto. Probá de nuevo.");
  const disparo = await fireTnImport({ ...destino, productos: [producto.importar] });
  if (!disparo.ok) throw new ErrorDeLaTienda(disparo.motivo ?? "error", "No pudimos traer este producto. Probá de nuevo.");
  const hasta = Date.now() + ESPERA_IMPORT_MS;
  while (Date.now() < hasta) {
    await sleep(2000);
    let q = supabase
      .from("productos")
      .select("id")
      .eq("id_company", destino.companyId)
      .eq("tn_product_id", producto.importar.tn_product_id);
    q = destino.brandId ? q.or(`brand_id.eq.${destino.brandId},brand_id.is.null`) : q.is("brand_id", null);
    const { data } = await q.limit(1);
    const id = (data?.[0] as { id?: string } | undefined)?.id;
    if (id) return id;
  }
  throw new ErrorDeLaTienda("demora", "Tu tienda está tardando en mandarnos el producto. Probá de nuevo en un minuto.");
}

/** El lanzamiento invisible de esta marca y tienda; lo crea la primera vez. */
export async function lanzamientoDeLaTienda(p: {
  companyId: string;
  brandId: string | null;
  plataforma: PlataformaDeTienda;
  userId: string | undefined;
}): Promise<string> {
  const buscar = async () => {
    let q = supabase
      .from("lanzamientos")
      .select("id")
      .eq("id_company", p.companyId)
      .eq("canal", p.plataforma)
      .eq("de_la_tienda", true);
    q = p.brandId ? q.eq("brand_id", p.brandId) : q.is("brand_id", null);
    const { data } = await q.limit(1);
    return (data?.[0] as { id?: string } | undefined)?.id ?? null;
  };
  const existente = await buscar();
  if (existente) return existente;
  const { data, error } = await supabase
    .from("lanzamientos")
    .insert({
      id_company: p.companyId,
      brand_id: p.brandId,
      nombre: p.plataforma === "tiendanube" ? "Tiendanube" : "Mercado Libre",
      canal: p.plataforma,
      de_la_tienda: true,
      created_by: p.userId ?? null,
    })
    .select("id")
    .single();
  if (data?.id) return data.id as string;
  // Otra pestaña lo creó en el medio (el índice único lo frena): se usa ése.
  const otra = await buscar();
  if (otra) return otra;
  console.error("[productosDeLaTienda] crear el lanzamiento de la tienda", error);
  throw new ErrorDeLaTienda("error", "No pudimos abrir el producto. Probá de nuevo.");
}

/**
 * Abre un producto de Tiendanube: lo trae a Atlas si hace falta, lo suma al lanzamiento invisible (la
 * ficha arranca igual a lo publicado) y devuelve a dónde navegar.
 */
export async function abrirProductoDeTiendanube(
  producto: ProductoDeLaTienda,
  destino: { userId: string | undefined; companyId: string; brandId: string | null },
  alAvanzar?: (paso: "trayendo" | "abriendo") => void,
): Promise<string> {
  const { lanzamientoId, filaId } = await filaDeProductoDeTiendanube(producto, destino, alAvanzar);
  return filaId ? `/lanzamientos/${lanzamientoId}?producto=${filaId}` : `/lanzamientos/${lanzamientoId}`;
}

/**
 * La fila de un producto de Tiendanube en el lanzamiento invisible de la tienda: lo trae a Atlas si hace
 * falta y lo suma (la ficha arranca igual a lo publicado). La usan abrir un producto y la edición masiva.
 */
export async function filaDeProductoDeTiendanube(
  producto: ProductoDeLaTienda,
  destino: { userId: string | undefined; companyId: string; brandId: string | null },
  alAvanzar?: (paso: "trayendo" | "abriendo") => void,
): Promise<{ lanzamientoId: string; filaId: string | null }> {
  let productoId = producto.productoId;
  if (!productoId) {
    alAvanzar?.("trayendo");
    productoId = await traerDeTiendanube(producto, destino);
  }
  alAvanzar?.("abriendo");
  const lanzamientoId = await lanzamientoDeLaTienda({ ...destino, plataforma: "tiendanube" });
  const fila = async () => {
    const { data } = await supabase
      .from("lanzamientos_productos")
      .select("id")
      .eq("lanzamiento_id", lanzamientoId)
      .eq("producto_id", productoId)
      .limit(1);
    return (data?.[0] as { id?: string } | undefined)?.id ?? null;
  };
  let filaId = await fila();
  if (!filaId) {
    const r = await agregarProductosDelCatalogo(lanzamientoId, destino.companyId, [productoId]);
    if (r.error) throw new ErrorDeLaTienda("error", "No pudimos abrir el producto. Probá de nuevo.");
    filaId = await fila();
  }
  return { lanzamientoId, filaId };
}

/**
 * "Agregar producto": un producto nuevo, con la ficha en blanco, en el lanzamiento de la tienda. Detrás
 * va un producto de Atlas (con su variante, como el alta de Mi marca): la fila necesita uno para crearse
 * en la tienda al publicar. Devuelve a dónde navegar.
 */
export async function crearProductoEnBlanco(destino: {
  userId: string | undefined;
  companyId: string;
  brandId: string | null;
}): Promise<string> {
  const { lanzamientoId, filaId } = await crearProductoNuevo(destino);
  return `/lanzamientos/${lanzamientoId}?producto=${filaId}`;
}

/**
 * Un producto nuevo de la tienda: en el catálogo de Atlas y en la lista de cambios de la tienda, con la
 * ficha en blanco (o con el nombre que se le pase). Se crea en la tienda al sincronizar. Lo usan "Agregar
 * producto" y la importación de una planilla.
 */
export async function crearProductoNuevo(
  destino: { userId: string | undefined; companyId: string; brandId: string | null },
  nombre = "",
  lanzamiento?: string,
): Promise<{ lanzamientoId: string; filaId: string }> {
  const lanzamientoId = lanzamiento ?? (await lanzamientoDeLaTienda({ ...destino, plataforma: "tiendanube" }));
  const fallo = () => new ErrorDeLaTienda("error", "No pudimos crear el producto. Probá de nuevo.");
  const { data: producto, error } = await supabase
    .from("productos")
    .insert({
      id_company: destino.companyId,
      nombre: nombre || "Producto nuevo",
      tiene_variantes: false,
      ...(destino.brandId ? { brand_id: destino.brandId } : {}),
    })
    .select("id")
    .single();
  if (error || !producto?.id) throw fallo();
  const productoId = String(producto.id);
  const { error: eVariante } = await supabase
    .from("variantes")
    .insert({ id_company: destino.companyId, nombre: nombre || "Producto nuevo", activo: true, product_id: productoId });
  if (eVariante) {
    // Sin variante el producto no se ve en ningún lado: no se deja huérfano.
    await supabase.from("productos").delete().eq("id", productoId).eq("id_company", destino.companyId);
    throw fallo();
  }
  const r = await agregarProductosDelCatalogo(lanzamientoId, destino.companyId, [productoId]);
  if (r.error) throw fallo();
  const { data: filas } = await supabase
    .from("lanzamientos_productos")
    .select("id")
    .eq("lanzamiento_id", lanzamientoId)
    .eq("producto_id", productoId)
    .limit(1);
  const filaId = (filas?.[0] as { id?: string } | undefined)?.id;
  if (!filaId) throw fallo();
  // La ficha arranca en blanco: el nombre de Atlas ("Producto nuevo") es sólo para el catálogo. Va `""` y
  // no null (`actualizarFicha` guarda null, y un nombre nulo cae al del catálogo).
  await supabase.from("lanzamientos_productos").update({ nombre }).eq("id", filaId);
  return { lanzamientoId, filaId };
}

/**
 * La variante de Atlas de una publicación de Mercado Libre, para el bloque de Mercado Libre de Editar
 * producto. Si la publicación no está en Atlas, se trae primero.
 */
export async function varianteDeMercadoLibre(
  producto: ProductoDeLaTienda,
  destino: { companyId: string; brandId: string | null },
  alAvanzar?: (paso: "trayendo") => void,
): Promise<{ productoId: string; varianteId: string }> {
  let productoId = producto.productoId;
  if (!productoId) {
    alAvanzar?.("trayendo");
    const ejecucion = await importarDeMl(destino.brandId, [{ item_ids: [producto.idExterno] }]);
    await esperarImportDeMl(ejecucion);
    const r = await pedir<{ producto_id?: string | null }>(
      `/v1/tienda/vinculo?${conMarca(destino.brandId, { item_id: producto.idExterno })}`,
    );
    productoId = r.producto_id ?? null;
    if (!productoId) throw new ErrorDeLaTienda("demora", "Todavía estamos trayendo esta publicación. Probá de nuevo en un minuto.");
  }
  const { data } = await supabase
    .from("variantes")
    .select("id")
    .eq("product_id", productoId)
    .eq("id_company", destino.companyId)
    .limit(1);
  const varianteId = (data?.[0] as { id?: string } | undefined)?.id;
  if (!varianteId) throw new ErrorDeLaTienda("error", "No encontramos este producto en Atlas.");
  return { productoId, varianteId };
}
